import { Box, Button, Group, Text, UnstyledButton } from '@mantine/core';
import { createReference } from '@medplum/core';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { IconArrowDownLeft, IconArrowUpRight, IconFileText } from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { CSSProperties, ReactElement, useMemo } from 'react';
import { HubTable } from '@/components/Hub/HubTable';
import { ORBIT_DISABLED, ORBIT_GRAY, ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';
import { ConnectPatientPopover } from '../ledger/ConnectPatientPopover';
import { CONNECT_BUTTON_STYLE } from '../ledger/ledgerConnect';
import { LedgerCell, LedgerEmptyCell } from '../ledger/LedgerCell';
import { formatLedgerDate, formatLedgerMoney } from '../ledger/ledgerRows';
import { ImportPatientMatch, ImportPick, ImportPreviewRow, previewCounts, SKIP_REASON_LABEL } from './importPreview';
import '../ledger/ledger.css';

/** The prototype's import preview: what the file would add, skipped rows dimmed. */

const MONEY_STYLE: CSSProperties = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
const RIGHT = { align: 'right' as const };

const SKIPPED_OPACITY = 0.42;

function StatusDot({ fresh }: { fresh: boolean }): ReactElement {
  return (
    <Box
      component="span"
      style={{
        width: 7,
        height: 7,
        borderRadius: 999,
        flexShrink: 0,
        background: fresh ? ORBIT_SETTLED : ORBIT_DISABLED,
      }}
    />
  );
}

/** A 22px disc, arrow in or out; out is amber because a refund is the exception. */
function DirectionIcon({ refund }: { refund: boolean }): ReactElement {
  const Icon = refund ? IconArrowUpRight : IconArrowDownLeft;
  return (
    <Box
      component="span"
      style={{
        width: 22,
        height: 22,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: refund ? 'rgba(138, 90, 18, 0.12)' : 'rgba(0, 0, 0, 0.05)',
        color: refund ? ORBIT_NEEDS_ACTION : ORBIT_GRAY,
      }}
    >
      <Icon size={13} stroke={2} />
    </Box>
  );
}

export interface ImportPreviewTableProps {
  fileName: string;
  rows: ImportPreviewRow[];
  picks: Record<string, ImportPick>;
  suggestions: Record<string, ImportPatientMatch>;
  importing: boolean;
  onPick: (reference: string, pick: ImportPick | null) => void;
  onCancel: () => void;
  onImport: () => void;
}

export function ImportPreviewTable({
  fileName,
  rows,
  picks,
  suggestions,
  importing,
  onPick,
  onCancel,
  onImport,
}: ImportPreviewTableProps): ReactElement {
  const counts = previewCounts(rows, picks);

  const columns = useMemo((): MRT_ColumnDef<ImportPreviewRow>[] => {
    const patientCell = (row: ImportPreviewRow): ReactElement => {
      if (!row.connectable) return <LedgerEmptyCell />;
      const pick = picks[row.reference];
      if (pick) {
        return (
          <Group gap={4} wrap="nowrap" className="ledger-patient" style={{ width: '100%' }}>
            <LedgerCell>{pick.name}</LedgerCell>
            <UnstyledButton
              title="Disconnect"
              aria-label="Disconnect"
              onClick={() => onPick(row.reference, null)}
              style={{ color: ORBIT_DISABLED, fontSize: 13, lineHeight: 1, padding: '0 2px' }}
            >
              ×
            </UnstyledButton>
          </Group>
        );
      }
      const choose = (iconTitle?: string): ReactElement => (
        <ConnectPatientPopover
          row={{ payerName: row.patientName ?? row.payerName, amount: row.amount }}
          iconTitle={iconTitle}
          onPick={(candidate) =>
            onPick(row.reference, {
              patient: createReference(candidate.patient),
              name: formatGivenFamilyName(candidate.patient.name?.[0]),
            })
          }
        />
      );
      const suggestion = suggestions[row.reference];
      return suggestion ? (
        <Group gap={6} wrap="nowrap">
          <UnstyledButton
            style={CONNECT_BUTTON_STYLE}
            onClick={() => onPick(row.reference, { patient: suggestion.patient, name: suggestion.name })}
          >
            Use {suggestion.name}
          </UnstyledButton>
          {choose('Choose another patient')}
        </Group>
      ) : (
        choose()
      );
    };

    return [
      {
        id: 'status',
        header: 'Status',
        size: 60,
        enableSorting: false,
        Cell: ({ row }) => {
          const fresh = row.original?.status === 'new';
          const reason = row.original?.reason;
          return (
            <Group gap={6} wrap="nowrap" title={reason ? SKIP_REASON_LABEL[reason] : undefined}>
              <StatusDot fresh={fresh} />
              <Text fz={13}>{fresh ? 'New' : 'Skip'}</Text>
            </Group>
          );
        },
      },
      {
        id: 'direction',
        header: '',
        size: 22,
        enableSorting: false,
        Cell: ({ row }) => <DirectionIcon refund={row.original?.kind === 'refund'} />,
      },
      {
        id: 'date',
        header: 'Date',
        size: 100,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell>{row.original?.occurredAt ? formatLedgerDate(row.original.occurredAt) : ''}</LedgerCell>
        ),
      },
      {
        id: 'payer',
        header: 'Payer',
        size: 140,
        enableSorting: false,
        Cell: ({ row }) => (
          <LedgerCell title={row.original?.payerName ?? undefined}>{row.original?.payerName}</LedgerCell>
        ),
      },
      {
        id: 'patient',
        header: 'Patient',
        size: 180,
        grow: true,
        enableSorting: false,
        Cell: ({ row }) => (row.original?.status === 'new' ? patientCell(row.original) : <LedgerEmptyCell />),
      },
      {
        id: 'amount',
        header: 'Amount',
        size: 100,
        enableSorting: false,
        mantineTableHeadCellProps: RIGHT,
        mantineTableBodyCellProps: RIGHT,
        Cell: ({ row }) => <LedgerCell style={MONEY_STYLE}>{formatLedgerMoney(row.original?.amount ?? 0)}</LedgerCell>,
      },
      {
        id: 'fee',
        header: 'Merchant fee',
        size: 96,
        enableSorting: false,
        mantineTableHeadCellProps: RIGHT,
        mantineTableBodyCellProps: RIGHT,
        Cell: ({ row }) => {
          const fee = row.original?.fee ?? 0;
          return (
            <LedgerCell style={MONEY_STYLE} color={fee > 0 ? ORBIT_SETTLED : undefined}>
              {fee ? formatLedgerMoney(fee, { sign: true }) : '$0.00'}
            </LedgerCell>
          );
        },
      },
      {
        id: 'net',
        header: 'Net',
        size: 100,
        enableSorting: false,
        mantineTableHeadCellProps: RIGHT,
        mantineTableBodyCellProps: RIGHT,
        Cell: ({ row }) => (
          <LedgerCell style={MONEY_STYLE} weight={600}>
            {formatLedgerMoney(row.original?.net ?? 0)}
          </LedgerCell>
        ),
      },
      {
        id: 'reference',
        header: 'Reference',
        size: 128,
        enableSorting: false,
        Cell: ({ row }) => <LedgerCell title={row.original?.reference}>{row.original?.reference}</LedgerCell>,
      },
    ];
  }, [picks, suggestions, onPick]);

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Group
        gap={8}
        px={12}
        py={10}
        wrap="wrap"
        style={{ background: '#fff', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}
      >
        <Group gap={7} wrap="nowrap">
          <IconFileText size={15} stroke={1.7} color={ORBIT_GRAY} />
          <Text fz={13} fw={600}>
            {fileName}
          </Text>
        </Group>
        <Text fz={12} c={ORBIT_GRAY}>
          <Text span fz={12} fw={600} c="black">
            {counts.fresh}
          </Text>{' '}
          new ·{' '}
          <Text span fz={12} fw={600} c="black">
            {counts.skipped}
          </Text>{' '}
          skipped
          {counts.noPatient > 0 && (
            <>
              {' · '}
              <Text span fz={12} c={ORBIT_NEEDS_ACTION}>
                {counts.noPatient} no patient
              </Text>
            </>
          )}
        </Text>
        <Group gap={8} ml="auto">
          <Button size="xs" variant="default" onClick={onCancel} disabled={importing}>
            Cancel
          </Button>
          <Button size="xs" color="dark" loading={importing} disabled={!counts.fresh} onClick={onImport}>
            Import {counts.fresh}
          </Button>
        </Group>
      </Group>
      <HubTable
        columns={columns}
        data={rows}
        loading={false}
        getRowProps={(row) => ({ style: row?.status === 'skip' ? { opacity: SKIPPED_OPACITY } : undefined })}
        emptyText="Nothing in this file"
      />
    </Box>
  );
}
